import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Trophy, TrendingUp, TrendingDown, Minus, Eye, RefreshCw } from 'lucide-react';
import { RatingBadge } from './RatingBadge';
import { PlayerDrawer } from './PlayerDrawer';
import { StatsService } from '@/services/stats';
import { PlayerStats } from '@/types/stats';
import { useLanguage } from '@/hooks/use-language';

const statsService = new StatsService();

export function Leaderboard() {
  const [players, setPlayers] = useState<PlayerStats[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  const loadPlayers = useCallback(async () => {
    try {
      const data = await statsService.getAllPlayers();
      setPlayers(data);
    } catch (error) {
      console.error('Failed to load players:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  const getPositionIcon = (position: number) => {
    if (position === 1) return <Trophy className="h-4 w-4 text-yellow-500" />;
    if (position === 2) return <Trophy className="h-4 w-4 text-gray-400" />;
    if (position === 3) return <Trophy className="h-4 w-4 text-amber-600" />;
    return <span className="text-sm font-semibold">{position}</span>;
  };

  const getFormIcon = (form: 'up' | 'down' | 'stable') => {
    if (form === 'up') return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (form === 'down') return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Player Rankings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">Loading rankings...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Player Rankings
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadPlayers}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Rank</TableHead>
                <TableHead>Player</TableHead>
                <TableHead>Level</TableHead>
                <TableHead className="text-center">Games</TableHead>
                <TableHead className="text-center">Win Rate</TableHead>
                <TableHead className="text-center">Form</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {players.map((player, index) => (
                <TableRow key={player.id}>
                  <TableCell className="text-center">
                    {getPositionIcon(index + 1)}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{player.name}</div>
                      {player.phone && (
                        <div className="text-sm text-muted-foreground">{player.phone}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <RatingBadge rating={player.rating} deviation={player.rating_deviation} />
                  </TableCell>
                  <TableCell className="text-center">{player.games_played}</TableCell>
                  <TableCell className="text-center">
                    {player.games_played > 0 ? `${Math.round(player.win_rate * 100)}%` : '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    {getFormIcon(player.recent_form)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedPlayer(player)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {players.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No players found. Player rankings will appear here as tournament matches are completed and recorded in the database.
            </div>
          )}
        </CardContent>
      </Card>

      <PlayerDrawer
        player={selectedPlayer}
        open={!!selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
      />
    </>
  );
}